import * as THREE from "three";
import { TouchControls } from "./touchcontrols";
import { hapticOnUiTaps, keepScreenReady } from "./touchdevice";
import { makeCollapsible } from "../../gui/widgets/collapsible";
import type { WASDController } from "./wasdcontroller";

export interface Size {
    width: number;
    height: number;
}

/**
 * Everything that differs between desktop and touch phones: viewport/canvas sizing, camera
 * gestures and the movement input source. Chosen once (see `platform` below); callers never
 * branch on `IS_TOUCH_PHONE` themselves.
 */
export interface PlatformStrategy {
    /** Layout size the canvas must fill (what position:fixed;inset:0 covers). */
    viewportSize(): Size;
    pixelRatio(): number;
    /** Third-person camera limits and gesture bindings. */
    configureCamera(controls: any): void;
    /** True when the WASD controller should run for the given movement mode. */
    drivesMovement(mode: string): boolean;
    /** Feed non-keyboard input into the WASD controller before it updates. */
    pollMovementInput(wasd: WASDController): void;
    /** Adjust the in-game HUD (e.g. collapsible chat/menu on phones). */
    enhanceHud(gameUI: any): void;
    enterGame(): void;
    leaveGame(): void;
}

class DesktopPlatform implements PlatformStrategy {
    viewportSize(): Size {
        return { width: window.innerWidth, height: window.innerHeight };
    }

    pixelRatio(): number {
        return 1;
    }

    configureCamera(controls: any): void {
        controls.minDistance = 0.8;
        controls.maxDistance = 15;
        controls.mouseButtons = {
            LEFT: -1, // Left drag is reserved for world interaction, not camera movement
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.ROTATE,
        };
    }

    drivesMovement(mode: string): boolean {
        return mode === "modern";
    }

    pollMovementInput(): void {}
    enhanceHud(): void {}
    enterGame(): void {}
    leaveGame(): void {}
}

class TouchPhonePlatform implements PlatformStrategy {
    viewportSize(): Size {
        // documentElement size is the layout viewport that the fixed canvas fills; window.inner*
        // can disagree with it on mobile browsers (toolbars, safe-area, scaled virtual viewport).
        const el = document.documentElement;
        return { width: el.clientWidth || window.innerWidth, height: el.clientHeight || window.innerHeight };
    }

    pixelRatio(): number {
        // The virtual viewport is scaled up by the browser; a 2x buffer keeps it from looking blurry.
        return Math.min(window.devicePixelRatio || 1, 2);
    }

    configureCamera(controls: any): void {
        // Desktop limits let a pinch zoom into the character's head (first-person look).
        controls.minDistance = 3;
        controls.maxDistance = 12;
        controls.minPolarAngle = 0.25;
        controls.maxPolarAngle = 1.35; // never below the horizon
        controls.rotateSpeed = 0.6;
        controls.zoomSpeed = 0.8;
        controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };
    }

    drivesMovement(): boolean {
        return true; // joystick: tapping the ground to walk isn't a thing on phones
    }

    pollMovementInput(wasd: WASDController): void {
        wasd.analog = TouchControls.analog;
    }

    enhanceHud(gameUI: any): void {
        makeCollapsible(gameUI.chatBox, "left");
        makeCollapsible(gameUI.menu, "right");
        hapticOnUiTaps((window as any).ui._element[0]);
    }

    enterGame(): void {
        keepScreenReady();
        TouchControls.show();
    }

    leaveGame(): void {
        TouchControls.hide();
    }
}

export const platform: PlatformStrategy = (window as any).IS_TOUCH_PHONE
    ? new TouchPhonePlatform()
    : new DesktopPlatform();

/** Client coordinates -> normalised device coordinates relative to the canvas, not the window. */
export function toNdc(clientX: number, clientY: number, canvas: HTMLElement): { x: number; y: number } {
    const r = canvas.getBoundingClientRect();
    return {
        x: ((clientX - r.left) / r.width) * 2 - 1,
        y: -((clientY - r.top) / r.height) * 2 + 1,
    };
}
